import * as THREE from 'three';
import { applyInteriorDark } from './interior';

// Procedural build-tier materials. Twig (lashed sticks), wood (planked boards), stone (coursed
// masonry), metal (corrugated riveted sheet), and reinforced (machined armor plate) are generated
// as tileable albedo + normal CanvasTextures from value noise and a little structure, so no image
// files are needed. Each tier yields one shared MeshStandardMaterial reused by every placed piece
// of that tier.

// The full 5-tier build ladder, weakest -> strongest. 'reinforced' is refined metal, the top tier
// (costs refined_metal from the oil refinery -- see BUILD.cost in config.ts).
export type BuildTier = 'twig' | 'wood' | 'stone' | 'metal' | 'reinforced';
export const BUILD_TIERS: BuildTier[] = ['twig', 'wood', 'stone', 'metal', 'reinforced'];
export const TIER_LABEL: Record<BuildTier, string> = {
  twig: 'Twig', wood: 'Wood', stone: 'Stone', metal: 'Metal', reinforced: 'Reinforced',
};

const SIZE = 512; // higher-res source so plank lines survive minification instead of blurring to mud

// One texture tile spans this many world units. Mesh factories scale their UVs to this density (see
// building.ts), so a 512px tile reads at a fixed ~world-uniform texel rate on every face regardless
// of the face's size — that is what stops the stretched, smeared planks. Roughly one full motif
// (4 plank courses / 5 masonry courses / the twig weave / a 2x2 panel grid) per WORLD_TILE metres.
export const WORLD_TILE = 1.5;

// Anisotropy. Defaults high so lazily-built textures (the Builder constructs its ghost before main.ts
// runs) are already crisp at grazing angles; setBuildTextureRenderer() upgrades it to the true GPU
// max and back-fills every texture already created, matching the terrain material's approach.
let maxAnisotropy = 16;
const liveTextures = new Set<THREE.Texture>();

export function setBuildTextureRenderer(renderer: THREE.WebGLRenderer): void {
  maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
  for (const t of liveTextures) { t.anisotropy = maxAnisotropy; t.needsUpdate = true; }
}

// `a` is coverage alpha (defaults opaque). Only twig emits it: the gaps between lashed sticks are cut
// out with alphaTest so you can partially see through a twig wall.
interface Sample { r: number; g: number; b: number; h: number; a?: number; }

function rng2(ix: number, iy: number, seed: number): number {
  let h = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(seed, 69069)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function clamp01(x: number): number { return x < 0 ? 0 : x > 1 ? 1 : x; }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

// Tileable value noise: an integer lattice of `period` cells across [0,1), indices wrapped.
function vnoise(u: number, v: number, period: number, seed: number): number {
  const x = u * period, y = v * period;
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const x0 = ((xi % period) + period) % period, x1 = (x0 + 1) % period;
  const y0 = ((yi % period) + period) % period, y1 = (y0 + 1) % period;
  const a = rng2(x0, y0, seed), b = rng2(x1, y0, seed), c = rng2(x0, y1, seed), d = rng2(x1, y1, seed);
  return lerp(lerp(a, b, sx), lerp(c, d, sx), sy);
}

function fbm(u: number, v: number, period: number, oct: number, seed: number): number {
  let amp = 1, sum = 0, norm = 0, p = period;
  for (let i = 0; i < oct; i++) {
    sum += amp * vnoise(u, v, p, seed + i * 131);
    norm += amp;
    amp *= 0.5;
    p *= 2;
  }
  return sum / norm;
}

// Anisotropic tileable value noise: independent integer lattice periods per axis. A small pu with a
// large pv stretches the field into long horizontal streaks -- wood grain, brushed-steel lines,
// rust runs. Periods must stay integers or the wrap breaks the tiling.
function vnoiseA(u: number, v: number, pu: number, pv: number, seed: number): number {
  const x = u * pu, y = v * pv;
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const x0 = ((xi % pu) + pu) % pu, x1 = (x0 + 1) % pu;
  const y0 = ((yi % pv) + pv) % pv, y1 = (y0 + 1) % pv;
  const a = rng2(x0, y0, seed), b = rng2(x1, y0, seed), c = rng2(x0, y1, seed), d = rng2(x1, y1, seed);
  return lerp(lerp(a, b, sx), lerp(c, d, sx), sy);
}

function fbmA(u: number, v: number, pu: number, pv: number, oct: number, seed: number): number {
  let amp = 1, sum = 0, norm = 0, qu = pu, qv = pv;
  for (let i = 0; i < oct; i++) {
    sum += amp * vnoiseA(u, v, qu, qv, seed + i * 131);
    norm += amp;
    amp *= 0.5;
    qu *= 2; qv *= 2;
  }
  return sum / norm;
}

// --- per-tier samplers: (u,v in [0,1)) -> color + height, all tileable ---

// Stone: COURSED MASONRY. 5 horizontal courses per tile, each split into blocks whose joints are
// offset a random amount per course (a running bond, so vertical joints never stack). Blocks carry
// a per-block grey value + speckle/pitting; the joints read as recessed dark mortar.
function stone(u: number, v: number): Sample {
  const COURSES = 5, BLOCKS = 3, S0 = 131;
  const fy = v * COURSES;
  const row = Math.floor(fy), ry = fy - row;
  const wrow = ((row % COURSES) + COURSES) % COURSES;
  // Random joint offset per course keeps the bond broken across the tile seam (COURSES is odd, so a
  // parity offset would land two same-phase courses next to each other where the texture repeats).
  const off = rng2(wrow, 3, S0);
  const fx = (((u - off) % 1) + 1) % 1 * BLOCKS;
  const col = Math.floor(fx), bx = fx - col;
  const cv = rng2(col % BLOCKS, wrow, S0 + 9); // per-block value
  // Distances to the nearest joint, in TILE units so mortar is the same width both ways.
  const du = Math.min(bx, 1 - bx) / BLOCKS;
  const dv = Math.min(ry, 1 - ry) / COURSES;
  const inside = smoothstep(0.006, 0.03, Math.min(du, dv)); // 0 in mortar -> 1 on the block face
  const speck = fbm(u, v, 24, 4, S0 + 3) - 0.5;   // grain speckle
  const pit = fbm(u, v, 48, 2, S0 + 5) - 0.5;     // finer pitting
  const h = 0.18 + inside * (0.55 + 0.2 * cv + 0.12 * speck + 0.06 * pit);
  let base = 0.46 + 0.22 * cv + 0.13 * speck + 0.06 * pit;
  base = lerp(0.26, base, inside); // mortar is dark grey
  const warm = 0.03 * (rng2(col % BLOCKS, wrow, S0 + 21) - 0.5); // faint per-block warm/cool cast
  return { r: clamp01(base + warm + 0.02), g: clamp01(base + 0.01), b: clamp01(base * 0.99 - warm), h: clamp01(h) };
}

// Wood: horizontal PLANKED BOARDS. 4 courses per tile; each course is ONE long board wrapping the
// tile with a single butt joint at a random phase, and about a third of the rows hide even that
// joint. Average plank length lands ~3x the old two-boards-per-row look (owner: planks read too
// short, "too similar to stone"). Long anisotropic grain runs along the boards, some boards carry
// a dark ringed knot, and nail pairs pin the board ends. Seams recess so the normal map draws edges.
function wood(u: number, v: number): Sample {
  const ROWS = 4, S0 = 700;
  const fy = v * ROWS;
  const row = Math.floor(fy), ry = fy - row; // ry: 0..1 within one plank course
  const wrow = ((row % ROWS) + ROWS) % ROWS;
  const off = rng2(wrow, 1, S0); // butt-joint phase, random per row so joints never stack
  const joint = rng2(wrow, 5, S0 + 61) < 0.7; // ~2/3 of rows show a joint; the rest run as one continuous plank
  const su = (((u - off) % 1) + 1) % 1;     // 0 at the butt joint, wraps across the tile seam
  const tint = rng2(wrow, 9, S0 + 2);       // per-plank tint (one plank per row now)
  // Seam distances in TILE units (course is 1/ROWS tall; the board runs the full tile width).
  const dv = Math.min(ry, 1 - ry) / ROWS;
  const du = joint ? Math.min(su, 1 - su) : 0.5;
  const bevel = smoothstep(0.005, 0.028, dv) * smoothstep(0.004, 0.02, du); // 0 at seams -> 1 on the face
  // Grain: long streaks stretched along the board (few cells in u, many across v).
  const grain = fbmA(u, v, 2, 44, 3, S0 + 11);
  // Knot: roughly half the planks get one, at a random spot, with concentric rings.
  let knot = 0, ring = 0;
  if (rng2(wrow, 13, S0 + 31) < 0.55) {
    const kx = 0.1 + 0.8 * rng2(wrow, 17, S0 + 37), ky = 0.3 + 0.4 * rng2(wrow, 19, S0 + 41);
    const kd = Math.hypot(su - kx, (ry - ky) / ROWS);
    const krad = 0.02 + 0.018 * rng2(wrow, 23, S0 + 43);
    knot = smoothstep(krad, krad * 0.3, kd);
    ring = 0.5 + 0.5 * Math.sin(kd * 260 + rng2(wrow, 29, S0 + 47) * 6);
  }
  // Nail heads: a pair just in from each side of the butt joint; jointless rows nail into a fixed
  // stud line instead so every plank still reads fastened.
  const ny = wrow % 2 ? 0.32 : 0.68;
  const nd = joint
    ? Math.min(Math.hypot(su - 0.035, (ry - ny) / ROWS), Math.hypot(su - 0.965, (ry - (1 - ny)) / ROWS))
    : Math.min(Math.hypot(su - 0.25, (ry - ny) / ROWS), Math.hypot(su - 0.75, (ry - (1 - ny)) / ROWS));
  const nail = smoothstep(0.009, 0.004, nd);
  const h = clamp01(0.18 + 0.55 * bevel + 0.12 * (grain - 0.5) - 0.10 * knot * (0.5 + 0.5 * ring) + 0.16 * nail);
  const lo = [0.24, 0.15, 0.08], hi = [0.62, 0.44, 0.25];
  const lv = clamp01(0.34 + 0.38 * grain + 0.22 * (tint - 0.5));
  let r = lerp(lo[0], hi[0], lv), g = lerp(lo[1], hi[1], lv), b = lerp(lo[2], hi[2], lv);
  const dark = knot * (0.35 + 0.3 * ring); // knots swirl darker than the surrounding grain
  r = lerp(r, 0.20, dark); g = lerp(g, 0.12, dark); b = lerp(b, 0.06, dark);
  const seam = (1 - bevel) * 0.6;
  r *= 1 - seam; g *= 1 - seam; b *= 1 - seam;
  // Nail heads are dull grey steel over the wood colour.
  r = lerp(r, 0.32, nail); g = lerp(g, 0.33, nail); b = lerp(b, 0.35, nail);
  return { r: clamp01(r), g: clamp01(g), b: clamp01(b), h };
}

// Twig: LASHED STICKS. Thin vertical sticks with a visibly ROUND cross-section, bound to a few
// horizontal binders by dark cord wraps at every crossing. The gaps between sticks carry ZERO alpha
// (cut out via the material's alphaTest) so a twig wall is genuinely see-through in the cracks;
// stick density is doubled from the old weave so it still blocks most of the sight line.
function twig(u: number, v: number): Sample {
  const VN = 32, HN = 6;
  const fx = u * VN;
  const vi = Math.round(fx);
  const wvi = ((vi % VN) + VN) % VN;
  const jv = (rng2(wvi, 0, 500) - 0.5) * 0.5;
  const dxv = Math.abs(fx - (vi + jv));
  const vrad = 0.34 + 0.08 * (rng2(wvi, 0, 531) - 0.5);
  const vstick = smoothstep(vrad, vrad * 0.2, dxv);
  const round = 1 - (dxv / vrad) * (dxv / vrad); // cylindrical shading across the stick: bright crown, dim flanks
  const fyy = v * HN;
  const hi2 = Math.round(fyy);
  const whi = ((hi2 % HN) + HN) % HN;
  const jh = (rng2(0, whi, 560) - 0.5) * 0.4;
  const dyh = Math.abs(fyy - (hi2 + jh));
  const hstick = smoothstep(0.3, 0.06, dyh);
  const stick = Math.max(vstick * 0.92, hstick); // binders ride on top
  // Lashing: where a stick crosses a binder, diagonal cord bands wrap the joint.
  const cross = vstick * hstick;
  const lash = cross * (0.5 + 0.5 * Math.sin(((fx - vi) + (fyy - hi2)) * Math.PI * 5));
  const along = fbm(u, v * 3, 24, 3, 590);
  const h = clamp01(0.06 + 0.62 * stick + 0.18 * clamp01(round) * vstick + 0.12 * (along - 0.5) * stick + 0.10 * lash);
  const tan = [0.46, 0.40, 0.29], tanHi = [0.67, 0.58, 0.43], gap = [0.05, 0.05, 0.05];
  const lv = clamp01(0.28 + 0.6 * (along - 0.2) + 0.35 * clamp01(round));
  let sr = lerp(tan[0], tanHi[0], lv), sg = lerp(tan[1], tanHi[1], lv), sb = lerp(tan[2], tanHi[2], lv);
  // Cord reads as darker fibre over the sticks.
  sr = lerp(sr, 0.30, lash * 0.8); sg = lerp(sg, 0.24, lash * 0.8); sb = lerp(sb, 0.15, lash * 0.8);
  return {
    r: clamp01(lerp(gap[0], sr, stick)),
    g: clamp01(lerp(gap[1], sg, stick)),
    b: clamp01(lerp(gap[2], sb, stick)),
    h,
    a: smoothstep(0.3, 0.55, stick), // gaps go transparent; edge texels stay dark so mips read as shadowed sticks
  };
}

// Metal: CORRUGATED RIVETED SHEET. A 2x2 panel grid per tile; each panel ripples with vertical
// corrugation ridges, rivet rows run along every seam, edges wear bright, and rust streaks bleed
// down the sheet from the horizontal joints. Deliberately rougher/dirtier than reinforced.
function metal(u: number, v: number): Sample {
  const PAN = 2, S0 = 900;
  const fx = u * PAN, fy = v * PAN;
  const px = Math.floor(fx), py = Math.floor(fy);
  const lx = fx - px, ly = fy - py; // 0..1 within one panel
  const wx = ((px % PAN) + PAN) % PAN, wy = ((py % PAN) + PAN) % PAN;
  const tint = rng2(wx, wy, S0 + 7); // per-panel paint variation
  // Corrugation: rounded vertical ridges, 9 per panel.
  const cor = 0.5 - 0.5 * Math.cos(lx * 9 * Math.PI * 2);
  // Panel seams (distances in TILE units; panels are square so u and v weigh the same).
  const du = Math.min(lx, 1 - lx) / PAN, dv = Math.min(ly, 1 - ly) / PAN;
  const edge = Math.min(du, dv);
  const face = smoothstep(0.004, 0.016, edge); // 0 on the seam line
  // Rivets: 6 per edge, sitting just inside every panel border.
  const RN = 6, inset = 0.045;
  const rl = (Math.floor(lx * RN) + 0.5) / RN, rc = (Math.floor(ly * RN) + 0.5) / RN;
  const rd = Math.min(
    Math.hypot((lx - rl) / PAN, (ly - inset) / PAN), Math.hypot((lx - rl) / PAN, (ly - (1 - inset)) / PAN),
    Math.hypot((lx - inset) / PAN, (ly - rc) / PAN), Math.hypot((lx - (1 - inset)) / PAN, (ly - rc) / PAN),
  );
  const rivet = smoothstep(0.011, 0.005, rd);
  // Rust: streaks hang below the top seam of each panel, varying along u only (weathering runs
  // straight down); a touch more rust pools in the corrugation valleys.
  const streak = smoothstep(0.5, 0.8, fbmA(u, (wy + 0.5) / PAN, 24, 1, 2, S0 + 13));
  const rust = clamp01(streak * (1 - smoothstep(0.02, 0.55, ly)) + 0.12 * (1 - cor) * streak);
  // Edge wear: a bright scuffed band just inside the seams where sheets rub.
  const wearNoise = fbm(u, v, 32, 2, S0 + 17);
  const wear = (smoothstep(0.005, 0.016, edge) - smoothstep(0.02, 0.05, edge)) * smoothstep(0.35, 0.7, wearNoise);
  const scratch = fbmA(u, v, 3, 90, 2, S0 + 23) - 0.5; // faint horizontal handling scratches
  const h = clamp01(0.12 + face * (0.28 + 0.34 * cor) + 0.22 * rivet - 0.06 * rust + 0.04 * scratch);
  // Albedo: with partial metalness the map feeds diffuse AND tints the reflection, so the base grey
  // stays bright (lifted after the glancing-angle near-black feedback).
  let val = 0.56 + 0.10 * (tint - 0.5) + 0.10 * (cor - 0.5) + 0.10 * scratch + 0.10 * wear;
  let r = val * 0.96, g = val, b = val * 1.05;
  const rustC = [0.45, 0.24, 0.11];
  r = lerp(r, rustC[0], rust * 0.85); g = lerp(g, rustC[1], rust * 0.85); b = lerp(b, rustC[2], rust * 0.85);
  const seamDark = (1 - face) * 0.55;
  r *= 1 - seamDark; g *= 1 - seamDark; b *= 1 - seamDark;
  r = lerp(r, 0.62, rivet * 0.7); g = lerp(g, 0.63, rivet * 0.7); b = lerp(b, 0.66, rivet * 0.7);
  return { r: clamp01(r), g: clamp01(g), b: clamp01(b), h };
}

// Reinforced: ARMORED BULKHEAD, the refined-metal top tier. ONE huge plate per tile (world-anchored
// UVs let plates flow across whole walls) with a recessed X CROSS-BRACE across each plate -- the
// armored signature that separates it from every flat sheet tier at a glance -- plus hairline
// machined seams, chamfer catch-light, countersunk bolts around the border, and a brushed sheen.
// Darker gunmetal-blue than raw metal (which stays a warm rusty grey).
function reinforced(u: number, v: number): Sample {
  const PAN = 1, S0 = 1000;
  const fy = v * PAN;
  const row = Math.floor(fy), ly = fy - row;
  const wrow = ((row % 4) + 4) % 4;
  const fx = ((u % 1) + 1) % 1 * PAN;
  const col = Math.floor(fx), lx = fx - col;
  const tint = rng2(col % 4, wrow, S0 + 3); // per-plate rolled-batch tint, very subtle
  // Hairline seams + chamfer (distances in TILE units; plates are square).
  const edge = Math.min(Math.min(lx, 1 - lx), Math.min(ly, 1 - ly)) / PAN;
  const face = smoothstep(0.0015, 0.007, edge);
  const cham = smoothstep(0.005, 0.01, edge) * (1 - smoothstep(0.013, 0.024, edge)); // machined bevel highlight
  // X CROSS-BRACE: two recessed diagonal channels, fading before they reach the seams so each plate
  // reads as an independently braced panel. The height drop lets the normal map bevel them for free.
  const d1 = Math.abs(lx - ly), d2 = Math.abs(lx + ly - 1);
  const brace = Math.max(smoothstep(0.045, 0.02, d1), smoothstep(0.045, 0.02, d2)) * smoothstep(0.03, 0.09, edge);
  // Countersunk bolts: corners + edge midpoints, 8 per plate. Bowl-shaped recess with a raised
  // socket nub in the middle.
  const bx = lx < 0.30 ? 0.10 : lx > 0.70 ? 0.90 : 0.50;
  const by = ly < 0.30 ? 0.10 : ly > 0.70 ? 0.90 : 0.50;
  let bolt = 0, nub = 0;
  if (bx !== 0.50 || by !== 0.50) { // plate centres stay clear
    const bd = Math.hypot((lx - bx) / PAN, (ly - by) / PAN);
    bolt = smoothstep(0.018, 0.006, bd);
    nub = smoothstep(0.006, 0.002, bd);
  }
  // Brushed sheen: very fine lines running along u (low period along, high across).
  const brush = fbmA(u, v, 2, 160, 2, S0 + 9) - 0.5;
  const tone = fbm(u, v, 6, 2, S0 + 11) - 0.5; // broad soft tonal drift so big plates aren't dead flat
  const h = clamp01(0.20 + 0.50 * face + 0.10 * cham - 0.14 * brace - 0.16 * bolt + 0.06 * nub + 0.03 * brush);
  let val = 0.54 + 0.05 * (tint - 0.5) + 0.06 * brush + 0.05 * tone + 0.10 * cham - 0.09 * brace - 0.10 * bolt + 0.04 * nub; // gunmetal: darker than raw metal so the tiers never read alike
  val *= lerp(0.45, 1, face); // hairline seams go dark
  // Cool steel, pushed bluer than before: raw metal stays warm, reinforced reads cold and machined.
  return { r: clamp01(val * 0.9), g: clamp01(val * 0.97), b: clamp01(val * 1.12), h };
}

const SAMPLERS: Record<BuildTier, (u: number, v: number) => Sample> = { twig, wood, stone, metal, reinforced };
// Relief strength of the baked normal map. Kept moderate: the previous values (twig 2.6 / wood 2.4 /
// stone 3.2) combined with the stretched UVs drove the seams almost black under the directional
// light — the "dark blotchy blending" along plank edges. Lower slopes keep the stylized depth
// without crushing the seams to mud. Metal runs hotter (corrugation must ripple); reinforced stays
// low so the plates read machine-flat.
const NORMAL_STRENGTH: Record<BuildTier, number> = { twig: 1.6, wood: 1.5, stone: 1.9, metal: 2.0, reinforced: 1.2 };
// PBR response per tier: organic tiers are rough dielectrics. The two metal tiers sit in the
// weathered 0.45-0.6 metalness band (MODELING.md): near-1 metalness kills the diffuse term and
// leaves ONLY env reflection, which crushed whole walls to near-black at glancing angles under the
// low iso env. Partial metalness keeps the albedo map feeding diffuse, so the sheet stays readable
// from every camera angle; reinforced stays the glossier of the two.
const ROUGHNESS: Record<BuildTier, number> = { twig: 0.9, wood: 0.8, stone: 0.95, metal: 0.55, reinforced: 0.4 };
const METALNESS: Record<BuildTier, number> = { twig: 0, wood: 0, stone: 0, metal: 0.5, reinforced: 0.55 };
// Matching softer normalScale per tier (was a flat 1,1). Keeps grain readable, stops seam darkening.
const NORMAL_SCALE: Record<BuildTier, number> = { twig: 0.7, wood: 0.65, stone: 0.8, metal: 0.85, reinforced: 0.55 };

const rasterCache = new Map<BuildTier, { map: THREE.CanvasTexture; normal: THREE.CanvasTexture }>();
function rasterize(tier: BuildTier): { map: THREE.CanvasTexture; normal: THREE.CanvasTexture } {
  const cached = rasterCache.get(tier);
  if (cached) return cached;
  const sample = SAMPLERS[tier];
  const cv = document.createElement('canvas');
  cv.width = cv.height = SIZE;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(SIZE, SIZE);
  const hbuf = new Float32Array(SIZE * SIZE);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const s = sample((x + 0.5) / SIZE, (y + 0.5) / SIZE);
      const i = y * SIZE + x;
      hbuf[i] = s.h;
      img.data[i * 4] = clamp01(s.r) * 255;
      img.data[i * 4 + 1] = clamp01(s.g) * 255;
      img.data[i * 4 + 2] = clamp01(s.b) * 255;
      img.data[i * 4 + 3] = clamp01(s.a ?? 1) * 255; // coverage alpha (twig gaps); everything else opaque
    }
  }
  ctx.putImageData(img, 0, 0);
  const map = new THREE.CanvasTexture(cv);
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  // Trilinear mipmapping (the three.js defaults, set explicitly): minification across distance picks
  // a mip instead of aliasing, magnification stays linear, and anisotropy at the GPU max keeps the
  // plank lines sharp at the grazing angles in the screenshot rather than smearing to mud.
  map.generateMipmaps = true;
  map.minFilter = THREE.LinearMipmapLinearFilter;
  map.magFilter = THREE.LinearFilter;
  map.anisotropy = maxAnisotropy;
  liveTextures.add(map);

  // Normal map from the height buffer via Sobel, sampled with wrap so it tiles.
  const ncv = document.createElement('canvas');
  ncv.width = ncv.height = SIZE;
  const nctx = ncv.getContext('2d')!;
  const nimg = nctx.createImageData(SIZE, SIZE);
  const strength = NORMAL_STRENGTH[tier];
  const at = (x: number, y: number): number => hbuf[(((y % SIZE) + SIZE) % SIZE) * SIZE + (((x % SIZE) + SIZE) % SIZE)];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      const inv = 1 / Math.hypot(dx, dy, 1);
      const i = (y * SIZE + x) * 4;
      nimg.data[i] = (-dx * inv * 0.5 + 0.5) * 255;
      nimg.data[i + 1] = (-dy * inv * 0.5 + 0.5) * 255;
      nimg.data[i + 2] = (inv * 0.5 + 0.5) * 255;
      nimg.data[i + 3] = 255;
    }
  }
  nctx.putImageData(nimg, 0, 0);
  const normal = new THREE.CanvasTexture(ncv);
  normal.colorSpace = THREE.NoColorSpace;
  normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
  normal.generateMipmaps = true;
  normal.minFilter = THREE.LinearMipmapLinearFilter;
  normal.magFilter = THREE.LinearFilter;
  normal.anisotropy = maxAnisotropy;
  liveTextures.add(normal);
  const out = { map, normal };
  rasterCache.set(tier, out);
  return out;
}

const cache = new Map<string, THREE.MeshStandardMaterial>();

// --- SKIN VARIANTS (cosmetics-only monetization groundwork). A skin never touches gameplay stats
// (hp/cost/tier ladder are keyed on BuildTier alone, untouched here) -- it only recolors the SAME
// baked albedo texture per tier via a cheap HSL-ish tint pass, plus small roughness/metalness
// nudges. Two demo skins prove the pipe end to end; more just add entries below.
export type SkinId = 'weathered' | 'midnight';
interface SkinOverride {
  label: string;
  // Recolor applied per-texel over the tier's own albedo sample (keeps each tier's structure --
  // plank lines, masonry courses, rivets -- and only shifts hue/tint/saturation).
  tint(r: number, g: number, b: number, tier: BuildTier): [number, number, number];
  roughnessAdd?: number;   // added to the tier's base roughness, clamped 0..1
  metalness?: Partial<Record<BuildTier, number>>; // per-tier metalness override (e.g. "blued" metal)
}
export const SKIN_REGISTRY: Record<SkinId, SkinOverride> = {
  // Desaturated + a mossy green tint on stone/wood -- an abandoned-ruin read. Twig/metal/reinforced
  // get the same desaturate+tint so the whole tier ladder stays visually consistent under the skin.
  weathered: {
    label: 'Weathered',
    tint(r, g, b) {
      const lum = r * 0.3 + g * 0.59 + b * 0.11;
      const dr = lerp(r, lum, 0.45), dg = lerp(g, lum, 0.45), db = lerp(b, lum, 0.45); // desaturate
      return [clamp01(dr * 0.90), clamp01(dg * 1.00 + 0.02), clamp01(db * 0.86)]; // mossy green-grey cast
    },
    roughnessAdd: 0.08,
  },
  // Dark charcoal wood, blued/gunmetal metal -- a premium "night ops" read.
  midnight: {
    label: 'Midnight',
    tint(r, g, b, tier) {
      if (tier === 'metal' || tier === 'reinforced') {
        // Blued steel: crush toward a cool dark navy, keep only the brightest highlights (rivets/bevels).
        const lum = r * 0.3 + g * 0.59 + b * 0.11;
        return [clamp01(lum * 0.32), clamp01(lum * 0.38), clamp01(lum * 0.55)];
      }
      // Charcoal wood/stone/twig: crush toward near-black with a faint cool tint.
      const lum = r * 0.3 + g * 0.59 + b * 0.11;
      return [clamp01(lum * 0.38), clamp01(lum * 0.40), clamp01(lum * 0.46)];
    },
    roughnessAdd: -0.05,
    metalness: { metal: 0.65, reinforced: 0.7 }, // bluer sheen reads best a touch more metallic
  },
};

function skinKey(tier: BuildTier, skinId?: SkinId | null): string {
  return skinId ? `${tier}:${skinId}` : tier;
}

// Re-tint an already-rasterized albedo CanvasTexture in place -> a NEW CanvasTexture (the base tier
// texture is shared/cached and must stay untouched). Cheap: runs once per tier+skin, then cached.
function retint(base: THREE.CanvasTexture, tier: BuildTier, skin: SkinOverride): THREE.CanvasTexture {
  const src = base.image as HTMLCanvasElement;
  const cv = document.createElement('canvas');
  cv.width = src.width; cv.height = src.height;
  const ctx = cv.getContext('2d')!;
  ctx.drawImage(src, 0, 0);
  const img = ctx.getImageData(0, 0, cv.width, cv.height);
  for (let i = 0; i < img.data.length; i += 4) {
    const [r, g, b] = skin.tint(img.data[i] / 255, img.data[i + 1] / 255, img.data[i + 2] / 255, tier);
    img.data[i] = r * 255; img.data[i + 1] = g * 255; img.data[i + 2] = b * 255;
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = maxAnisotropy;
  liveTextures.add(tex);
  return tex;
}

export function getTierMaterial(tier: BuildTier, skinId?: SkinId | null): THREE.MeshStandardMaterial {
  const key = skinKey(tier, skinId);
  let mat = cache.get(key);
  if (!mat) {
    const skin = skinId ? SKIN_REGISTRY[skinId] : null;
    const { map: baseMap, normal } = rasterize(tier); // rasterize() itself caches nothing -- see below
    const map = skin ? retint(baseMap, tier, skin) : baseMap;
    const ns = NORMAL_SCALE[tier];
    mat = new THREE.MeshStandardMaterial({
      map,
      normalMap: normal,
      normalScale: new THREE.Vector2(ns, ns),
      roughness: clamp01(ROUGHNESS[tier] + (skin?.roughnessAdd ?? 0)),
      metalness: skin?.metalness?.[tier] ?? METALNESS[tier],
    });
    // Twig gaps are cut out via alphaTest (transparent stays FALSE so depth-sorting stays sane).
    // Shadow maps ignore alphaTest by default, so twig still casts a solid shadow -- acceptable
    // for a stick wall.
    if (tier === 'twig') mat.alphaTest = 0.5;
    // Build pieces join the sealed-base interior dim: a NEIGHBOUR'S bright walls floating in the
    // OLOS blackness were the loudest leak. Your own room stays lit by the home radius.
    applyInteriorDark(mat);
    cache.set(key, mat);
  }
  return mat;
}

// Foundations read a HAIR darker + a touch rougher than walls/ceilings of the same tier -- an extremely minute cue
// (only really noticeable side-by-side) that a piece is a foundation. The clone shares the tier's textures (cheap;
// at most one extra material per tier), so there's no new canvas/GPU cost.
const foundationCache = new Map<string, THREE.MeshStandardMaterial>();
export function getFoundationMaterial(tier: BuildTier, skinId?: SkinId | null): THREE.MeshStandardMaterial {
  const key = skinKey(tier, skinId);
  let mat = foundationCache.get(key);
  if (!mat) {
    const base = getTierMaterial(tier, skinId);
    mat = base.clone();                                  // shares map/normalMap, own uniforms
    mat.color.multiplyScalar(0.92);                      // ~8% darker albedo: a ground-contact, weathered read
    mat.roughness = Math.min(1, base.roughness + 0.06);  // a touch more matte at the base
    applyInteriorDark(mat);                              // Material.clone() drops onBeforeCompile: re-apply the dim
    foundationCache.set(key, mat);
  }
  return mat;
}
